import { WorkflowDebuggerAuthGuard } from './modules/workflow-debugger/services/workflow-debugger-auth.guard';
import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { PageLoginComponent } from './pages/login/login.component';
import { HomepageComponent } from './pages/homepage/homepage.component';
import { ProjectManagerComponent } from './pages/project-manager/project-manager.component';
import { ProjectOverviewComponent } from './pages/project-overview/project-overview.component';
import { LoginAuthGuard } from './pages/login/login-auth-guard/login-auth.guard';
import { LoggedInLayoutComponent } from './shared/logged-in-layout/logged-in-layout.component';
import { UserCreationComponent } from './pages/user-creation/user-creation.component';
import { FileManagerComponent } from './pages/file-manager/file-manager.component';
import { AccountComponent } from './pages/account/account.component';
import { ProjectCreationComponent } from './pages/project-creation/project-creation.component';
import { CanDeactivateProjectGuard } from './pages/project-creation/can-deactivate-guard/can-deactivate.guard';
import { ProjectCreationFormComponent } from './pages/project-creation/project-creation-form/project-creation-form.component';
import { AvailableWorkflowsComponent } from './pages/project-creation/available-workflows/available-workflows.component';
import { UserCreationAuthGuard } from './pages/user-creation/user-creation-auth-guard/user-creation-auth.guard';

import { NotFoundComponent } from './shared/not-found/not-found.component';
import { ProjectOverviewNewComponent } from './pages/project-overview-new/project-overview-new.component';
import { TabsName } from '../app/shared/helpers/tabsName';
import { PagesName } from '../app/shared/helpers/pagesName';
import { TeamsManagerComponent } from './pages/teams-manager/teams-manager.component';
import { TeamManagerComponent } from './pages/teams-manager/team-manager/team-manager.component';
import { TeamAuthGuard } from './pages/teams-manager/team-manager/team-auth.guard';
import { ProjectConfigurationComponent } from './pages/project-creation/project-configuration/project-configuration.component';
import { ProjectDataTypesComponent } from './pages/project-creation/project-data-types/project-data-types.component';
import { WorkflowInProgressComponent } from './pages/project-creation/workflow-in-progress/workflow-in-progress.component';
import { PreselectedWorkflowComponent } from './pages/project-creation/preselected-workflow/preselected-workflow.component';
import { NewAccountComponent } from './pages/new-account/new-account.component';
import { ProjectDiseasesComponent } from './pages/project-creation/project-diseases/project-diseases.component';
import { WorkflowAuthGuard } from './modules/work-flow/workflow-auth.guard';
import { UserNotificationsPageComponent } from './pages/user-notifications-page/user-notifications-page.component';
import { ProjectOverview2Component } from './pages/project-overview2/project-overview2.component';
import { DlProxyComponent } from './modules/shared/proxy/dl-proxy.component';

const routes: Routes = [
  // component logged in layout contains all site pages, is guarded by authguard.
  {
    path: '',
    canActivate: [LoginAuthGuard],
    component: LoggedInLayoutComponent,
    children: [
      {
        path: '',
        component: HomepageComponent,
      },
      {
        path: PagesName.Home,
        redirectTo: '',
      },

      {
        path: PagesName.ProjectCreation,
        component: ProjectCreationComponent,

        children: [
          { path: '', redirectTo: PagesName.DefineProject, pathMatch: 'full' },
          {
            path: PagesName.DefineProject,
            component: ProjectCreationFormComponent,
            canDeactivate: [CanDeactivateProjectGuard],
          },
          {
            path: PagesName.DefineProject + '/:projectId' + '/:country',
            component: ProjectCreationFormComponent,
            canDeactivate: [CanDeactivateProjectGuard],
          },
          {
            path: PagesName.ChooseWorkflow + '/:projectId' + '/:country',
            component: AvailableWorkflowsComponent,
            canDeactivate: [CanDeactivateProjectGuard],
          },
          {
            path: PagesName.ChooseWorkflow + '/:projectId' + '/:workflowId' + '/:country',
            component: PreselectedWorkflowComponent,
            canDeactivate: [CanDeactivateProjectGuard],
          },
          {
            path: PagesName.ChooseDiseases + '/:projectId' + '/:workflowId' + '/:country',
            component: ProjectDiseasesComponent,
            canDeactivate: [CanDeactivateProjectGuard],
          },
          {
            path: PagesName.ChooseDataTypes + '/:projectId' + '/:workflowId' + '/:country',
            component: ProjectDataTypesComponent,
            canDeactivate: [CanDeactivateProjectGuard],
          },
          {
            path: PagesName.ConfigureWorkflow + '/:projectId' + '/:workflowId' + '/:country',
            component: ProjectConfigurationComponent,
            canDeactivate: [CanDeactivateProjectGuard],
          },
          {
            path: PagesName.WorkflowInProgress + '/:projectId' + '/:country',
            component: WorkflowInProgressComponent,
          },
        ],
      },

      {
        path: PagesName.ProjectManager,
        redirectTo: PagesName.ProjectManager + '/' + TabsName.MyProjects,
        pathMatch: 'full',
      },
      {
        path: PagesName.ProjectManager + '/:viewTab',
        component: ProjectManagerComponent,
      },
      {
        path: PagesName.ProjectManager + '/:viewTab/:id/:country' + '/' + PagesName.ProjectOverview,
        component: ProjectOverviewComponent,
      },
      {
        path: PagesName.FileManager,
        component: FileManagerComponent,
      },
      { path: PagesName.FileManager + ':id/dl', component: DlProxyComponent },
      { path: 'project-overview-new', component: ProjectOverviewNewComponent },
      {
        path: PagesName.DevTest,
        component: ProjectOverview2Component,
      },
      { path: PagesName.UserNotifications, component: UserNotificationsPageComponent },
      {
        path: PagesName.TeamsManager,
        component: TeamsManagerComponent,
        canActivate: [TeamAuthGuard],
      },
      {
        path: PagesName.TeamsManager + '/:id',
        component: TeamManagerComponent,
        canActivate: [TeamAuthGuard],
      },

      {
        path: PagesName.UserCreation,
        component: UserCreationComponent,
        canActivate: [UserCreationAuthGuard],
      },
      { path: PagesName.Account, component: AccountComponent },
      { path: 'explorer', component: HomepageComponent },
      {
        path: PagesName.Workflow,
        loadChildren: () => import('./modules/work-flow/work-flow.module').then((m) => m.WorkFlowModule),
        canActivate: [WorkflowAuthGuard],
      },
      {
        path: PagesName.WorkflowDebugger,
        loadChildren: () => import('./modules/workflow-debugger/workflow-debugger.module').then((m) => m.WorkflowDebuggerModule),
        canActivate: [WorkflowDebuggerAuthGuard],
      },
    ],
  },
  { path: PagesName.Login, component: PageLoginComponent },
  {
    path: PagesName.NewAccount + '/:xAccessToken',
    component: NewAccountComponent,
  },
  { path: '**', component: NotFoundComponent },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { relativeLinkResolution: 'legacy' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
